import { AppColors } from "@/styles/colors";
import { Button, CloseButton, Dialog, Flex, Portal, Text } from "@chakra-ui/react";
import { Dispatch, SetStateAction, useEffect } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { LightMode } from "../ui/color-mode";
import { SelectComponent } from "../select/Select";

const TIPO_CAMPO_OPTIONS = [
  { label: "Propio", value: "1" },
  { label: "Alquilado", value: "2" },
];

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  currentTipo?: string;
  loadingAccept: boolean;
  onAccept: (situacion: number) => Promise<void>;
}

export const ModificarTipoCampoModal = ({
  open,
  setOpen,
  currentTipo,
  loadingAccept,
  onAccept,
}: Props) => {
  const defaultValue = TIPO_CAMPO_OPTIONS.find(
    (o) => o.label.toLowerCase() === currentTipo?.toLowerCase(),
  )?.value;

  const { control, reset, getValues } = useForm<FieldValues>({
    defaultValues: { tipo_campo: defaultValue ? [defaultValue] : [] },
  });

  useEffect(() => {
    if (open) {
      const value = TIPO_CAMPO_OPTIONS.find(
        (o) => o.label.toLowerCase() === currentTipo?.toLowerCase(),
      )?.value;
      reset({ tipo_campo: value ? [value] : [] });
    }
  }, [open, currentTipo]);

  const tipoCampoValue = useWatch({ control, name: "tipo_campo" });

  const handleClose = () => {
    reset({ tipo_campo: [] });
    setOpen(false);
  };

  const handleAccept = async () => {
    const situacion = getValues("tipo_campo")?.[0];
    if (!situacion) return;
    await onAccept(Number(situacion));
  };

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={(e) => { if (!e.open) handleClose(); }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content data-testid="modificar-tipo-campo-modal">
              <Dialog.Header paddingY="16px" paddingLeft="24px">
                <Dialog.Title fontWeight={600} color={AppColors.GRAY_TEXT} fontSize="18px">
                  Modificar tipo de establecimiento
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body paddingX="24px" paddingY="8px">
                <Flex flexDir="column" gap="12px">
                  <Text fontSize="16px" color={AppColors.GRAY_TEXT}>
                    Seleccione si es propio o alquilado
                  </Text>
                  <SelectComponent
                    control={control}
                    name="tipo_campo"
                    placeholder="Seleccione una opción"
                    items={TIPO_CAMPO_OPTIONS}
                    fullWidth
                  />
                </Flex>
              </Dialog.Body>
              <Dialog.Footer paddingY="16px" paddingX="24px">
                <Dialog.ActionTrigger asChild>
                  <Button
                    variant="outline"
                    paddingX="16px"
                    fontWeight={600}
                    bgColor={AppColors.GRAY_BUTTON_BG}
                    onClick={handleClose}
                  >
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button
                  paddingX="16px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  fontWeight={600}
                  onClick={handleAccept}
                  loading={loadingAccept}
                  disabled={loadingAccept || !tipoCampoValue?.length}
                >
                  Aceptar
                </Button>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
